import React, { useState, useRef, useEffect } from "react";
import { setCookie, removeCookie } from "../ReusableFunctions/cookieHandlers";
import CustomAudioVideoControls from "./CustomAudioVideoControls";
import useHlsMedia from "../hooks/useHlsMedia";
import { parseStartSeconds } from "../ReusableFunctions/hlsSource";

const CustomAudioPlayer = ({
  audioUrl,
  handlePause,
  handlePlay,
  logRecentlyPlayed,
  modal = false,
  addEmbedCodeToMediaData,
  mediaData,
  setMediaData,
  checked = false,
  handleStartHereTime,
}) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [seeking, setIsSeeking] = useState(false);

  // Prefer the HLS stream URL when present; otherwise fall back to the direct file.
  const streamUrl = audioUrl.streamUrl;
  useHlsMedia(audioRef, streamUrl, parseStartSeconds(audioUrl.urlTimestamp));

  useEffect(() => {
    // Duration is tied to a useEffect so that the media and duration are available when the component mounts
    const audioElement = audioRef.current;
    const handleLoadedMetadata = () => {
      setDuration(audioElement.duration);
    };
    const handleBeforeUnload = () => {
      // Handles cookies when navigating away from the site or refreshing the page
      if (!modal) {
        audioElement.currentTime > 0
          ? setCookie(audioUrl.url, audioElement.currentTime)
          : removeCookie(audioUrl.url);
      }
    };
    function attachListeners() {
      audioElement.addEventListener("loadedmetadata", handleLoadedMetadata);
      window.addEventListener("beforeunload", handleBeforeUnload);
    }
    if (!audioElement) {
      // If no audio yet, wait for it to appear in the DOM
      const intervalId = setInterval(() => {
        if (audioElement) {
          clearInterval(intervalId);
          attachListeners();
        }
      }, 100);
    } else {
      attachListeners();
    }
    return () => {
      handleBeforeUnload();
      audioElement.removeEventListener("loadedmetadata", handleLoadedMetadata);
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  const handleTimeUpdate = () => {
    setCurrentTime(audioRef.current.currentTime);
    if (modal) {
      handleStartHereTime(audioRef.current.currentTime);
    }
  };

  return (
    <div className={`${modal ? "w-[328px] lg:w-[384px]" : ""}`}>
      <audio
        ref={audioRef}
        src={
          streamUrl
            ? undefined // useHlsMedia attaches the stream to the element
            : audioUrl.urlTimestamp
            ? audioUrl.urlTimestamp
            : audioUrl.url
        }
        preload="metadata"
        onTimeUpdate={() => {
          // Updating while dragging is handled in the handleSeeking function
          if (!seeking) {
            handleTimeUpdate();
          }
        }}
        onPlaying={() => setIsPlaying(true)}
        onPause={() => {
          setIsPlaying(false);
          if (!modal && currentTime > 0) {
            setCookie(audioUrl.url, currentTime);
          }
        }}
      />
      <div
        className={`flex items-center bg-primaryDark text-white ${
          modal
            ? "lg:rounded-bl-2xl min-h-[274.36px] pt-[192.36px] lg:min-h-[356px] lg:pt-[232px] px-3 py-2 lg:p-4 flex-col gap-4 lg:gap-7"
            : "xs:flex-col xs:w-full xs:min-h-[140px] sm:min-h-max sm:flex-row p-4 rounded-xl gap-7 p-4"
        } mt-[-1px]`}
      >
        <CustomAudioVideoControls
          mediaRef={audioRef}
          mediaUrl={audioUrl.url}
          modal={modal}
          isPlaying={isPlaying}
          currentTime={currentTime}
          setCurrentTime={setCurrentTime}
          duration={duration}
          handlePlay={handlePlay}
          logRecentlyPlayed={logRecentlyPlayed}
          handlePause={handlePause}
          seeking={seeking}
          setIsSeeking={setIsSeeking}
          handleStartHereTime={handleStartHereTime}
          addEmbedCodeToMediaData={addEmbedCodeToMediaData}
          setMediaData={setMediaData}
          mediaData={mediaData}
          checked={checked}
        />
      </div>
    </div>
  );
};

export default CustomAudioPlayer;
